import { useEffect, useRef, useState } from 'react';

const SLIDE_IMAGES = [
  '/assets/salon-1.jpeg',
  '/assets/salon-2.jpeg',
  '/assets/salon-3.jpeg',
  '/assets/salon-4.jpeg',
  '/assets/salon-galeria.jpg',
];

export default function Hero() {
  const sectionRef = useRef<HTMLElement>(null);
  const [visible, setVisible] = useState(false);
  const [currentSlide, setCurrentSlide] = useState(0);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setVisible(true);
          observer.disconnect();
        }
      },
      { threshold: 0.1 }
    );
    if (sectionRef.current) observer.observe(sectionRef.current);
    return () => observer.disconnect();
  }, []);

  // Slideshow timer
  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentSlide((prev) => (prev + 1) % SLIDE_IMAGES.length);
    }, 5000);
    return () => clearInterval(timer);
  }, []);

  return (
    <section
      id="inicio"
      ref={sectionRef}
      className="relative w-full h-[100dvh] min-h-[600px] flex flex-col items-center justify-center overflow-hidden"
    >
      {/* Background Slideshow */}
      <div className="absolute inset-0 bg-black">
        {SLIDE_IMAGES.map((src, index) => (
          <img
            key={src}
            src={src}
            alt={`Nono Tano ${index + 1}`}
            className={`absolute inset-0 w-full h-full object-cover object-center blur-[1px] transition-opacity duration-1000 ${
              index === currentSlide ? 'opacity-100' : 'opacity-0'
            }`}
          />
        ))}
        <div className="absolute inset-0 bg-gradient-to-b from-black/50 via-black/40 to-black/60" />
      </div>

      {/* Slide indicators */}
      <div className="absolute bottom-20 left-1/2 -translate-x-1/2 z-10 flex gap-2">
        {SLIDE_IMAGES.map((_, index) => (
          <button
            key={index}
            onClick={() => setCurrentSlide(index)}
            className={`h-1 rounded-full transition-all duration-500 ${
              index === currentSlide
                ? 'w-6 bg-white'
                : 'w-1.5 bg-white/40 hover:bg-white/60'
            }`}
            aria-label={`Ir a foto ${index + 1}`}
          />
        ))}
      </div>

      {/* Content */}
      <div
        className={`relative z-10 flex flex-col items-center text-center px-6 max-w-2xl mx-auto transition-all duration-1000 ${
          visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
        }`}
      >
        {/* Logo transparente */}
        <img
          src="/assets/logo-blanco.png"
          alt="Nono Tano"
          className="h-24 md:h-32 w-auto object-contain mb-10 drop-shadow-[0_4px_20px_rgba(0,0,0,0.5)]"
        />

        <div className="drop-shadow-[0_2px_10px_rgba(0,0,0,0.7)] mb-8">
          <h1 className="font-pacifico text-3xl md:text-5xl bg-[linear-gradient(to_right,#FFD93D,#FF5E78,#7BDEFF,#A8E6CF,#FFB8C6)] bg-clip-text text-transparent brightness-110 contrast-125">
            Próximamente
          </h1>
        </div>

        {/* Contacto básico */}
        <div className="flex flex-col sm:flex-row items-center gap-3 sm:gap-6 text-white/70 text-sm mb-8">
          <span className="inline-flex items-center gap-1.5">
            <div className="w-4 h-4 flex items-center justify-center">
              <i className="ri-map-pin-line" />
            </div>
            Córdoba, Argentina
          </span>
          <span className="hidden sm:block text-white/30">|</span>
          <span className="inline-flex items-center gap-1.5">
            <div className="w-4 h-4 flex items-center justify-center">
              <i className="ri-store-3-line" />
            </div>
            ¡Te esperamos!
          </span>
        </div>

        {/* Redes sociales */}
        <div className="flex items-center gap-4">
          <a
            href="https://instagram.com/nonotano.helados"
            target="_blank"
            rel="noopener noreferrer"
            className="w-11 h-11 flex items-center justify-center rounded-full bg-white/15 backdrop-blur-sm text-white hover:bg-white/30 hover:scale-110 transition-all duration-300"
            aria-label="Instagram"
          >
            <i className="ri-instagram-line text-lg" />
          </a>
          <a
            href="https://facebook.com/NonoTanoHelados"
            target="_blank"
            rel="noopener noreferrer"
            className="w-11 h-11 flex items-center justify-center rounded-full bg-white/15 backdrop-blur-sm text-white hover:bg-white/30 hover:scale-110 transition-all duration-300"
            aria-label="Facebook"
          >
            <i className="ri-facebook-circle-line text-lg" />
          </a>
          <a
            href="https://wa.me/5491112345678"
            target="_blank"
            rel="noopener noreferrer"
            className="w-11 h-11 flex items-center justify-center rounded-full bg-white/15 backdrop-blur-sm text-white hover:bg-white/30 hover:scale-110 transition-all duration-300"
            aria-label="WhatsApp"
          >
            <i className="ri-whatsapp-line text-lg" />
          </a>
        </div>
      </div>

      {/* Footer simple */}
      <div className="absolute bottom-6 left-0 right-0 z-10 text-center">
        <p className="text-xs text-white/40">
          © 2026 Nono Tano Heladería Artesanal
        </p>
      </div>
    </section>
  );
}